<template>
  <div>
    <div class="profile-content">
      <v-row>
        <v-col>
          <h1>Affiliate Info</h1>
        </v-col>
        <v-col v-if="this.affiliateInfo.canEdit && editMode" cols="2">
          <v-btn v-if="editMode" @click="abortEdit">cancel</v-btn>
        </v-col>
        <v-col v-if="this.affiliateInfo.canEdit" cols="2">
          <v-btn v-if="!editMode" @click="toggleEditMode" class="mt-2">edit</v-btn>
          <v-btn v-if="editMode" @click="saveEdits">save</v-btn>
        </v-col>
      </v-row>
      <v-card class="user-profile-card" v-if="!!affiliateInfo && !!affiliateInfo.contact">
        <v-card-title>
          <h3>
            {{ affiliateInfo.name }}
          </h3>
        </v-card-title>
        <v-card-text>
            <p>
                <b>ASCA Member ID: </b> {{ affiliateInfo.memberId }} <br />
                <span v-if="this.affiliateInfo.termOfOffice != 0">
                    <b>Terms of Office: </b> {{ affiliateInfo.termOfOffice }} <br />
                </span>
                <span v-if="editMode">
                    <v-text-field
                      label="Email"
                      v-model="affiliateInfo.contact.email"
                    />
                    <InternationalPhoneNumberInput
                      inline
                      :phone="affiliateInfo.contact.phoneNumber"
                      @changeData="(v) => affiliateInfo.contact.phoneNumber = v.number.e164"
                    />
                    <v-text-field
                      label="Address"
                      v-model="affiliateInfo.contact.address"
                    />
                    <v-text-field
                      label="City"
                      v-model="affiliateInfo.contact.city"
                    />
                    <state-dropdown
                      inline
                      :required="false"
                      :selectedState="affiliateInfo.contact.state"
                      v-on:update="address.state = $event"
                    />
                    <v-text-field
                      label="Zip Code"
                      v-model="affiliateInfo.contact.zipCode"
                    />
                    <v-text-field
                      label="Club Site"
                      v-model="affiliateInfo.clubSite"
                    />
                </span>
                <span v-else>
                    <b>Email: </b> {{ affiliateInfo.contact.email }} <br />
                    <b>Phone: </b> {{ affiliateInfo.contact.phoneNumber }} <br />
                    <b>Address: </b>
                    <span>
                        {{ affiliateInfo.contact.address }} <br />
                        {{ affiliateInfo.contact.city }}
                        {{ affiliateInfo.contact.state }}
                        {{ affiliateInfo.contact.zipCode }}
                    </span><br />
                    <b>Website: </b> <a :href="affiliateInfo.clubSite">{{ affiliateInfo.clubSite }}</a><br />
                </span>
            </p>
        </v-card-text>
      </v-card>
      <v-card class="user-profile-card">
        <v-card-title>
          <h3>Members/Officers/Directors</h3>
        </v-card-title>
        <v-card-text v-if="!!affiliateInfo.members">
          <v-simple-table
            fixed-header
            :height="Math.min(360, (affiliateInfo.members.length + 1) * 60)"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Position</th>
                  <th class="text-left">Name</th>
                  <th class="text-left">Member ID</th>
                  <th class="text-left"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in affiliateInfo.members"
                  :key="member.personId"
                >
                  <td>{{ member.position }}</td>
                  <td>{{ member.firstName }} {{ member.lastName }}</td>
                  <td>{{ member.memberId }}</td>
                  <td>
                    <v-tooltip dark right>
                      <template v-slot:activator="{ on, attrs }">
                        <font-awesome-icon
                          icon="id-card"
                          :color="member.active ? 'green' : 'red'"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <span>{{ member.active ? "Active" : "Expired" }}</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
      <v-card class="user-profile-card" v-if="!!affiliateInfo">
        <v-card-title>
          <h3>Renewal Info</h3>
        </v-card-title>
        <v-card-text> 
            <p>
                The expiration date for this affiliate club is  {{
            affiliateInfo.renewalDate
              ? $dateUtils.formatDate(new Date(affiliateInfo.renewalDate))
              : ""
                }}
            </p>
          <p v-if="!this.affiliateInfo.canRenew">You are ineligible to renew this club for the following reasons:
            <ul>
              <li v-for="reason in this.affiliateInfo.renewalMessages" :key="reason">{{reason}}</li>
            </ul>
          </p>
          <p v-else>
              The fee for renewing this affiliate club is ${{this.affiliateInfo.renewalAmount}}.
              <span v-if="affiliateInfo.lateFeeApplied">
                  <br />
                  <em>A late fee will be added because it is outside of the regular renewal dates of Dec 1 - Feb 15th.</em>
              </span>
              <br /><br />
                    <p v-if="affiliateInfo.message">
                        <strong>Please Note:</strong> <br />
                        <em>{{this.affiliateInfo.message}}</em>
                        <br /><br />
                    </p>
                    
              <router-link class="no-text-decoration mr-3"
                           :to="`/renew/affiliate/${affiliateInfo.affiliateId}`"
                           v-if="this.affiliateInfo.canRenew && this.affiliateInfo.canEdit">
                  <v-btn color="primary" class="white--text">Renew Now</v-btn>
              </router-link>
          </p>
        </v-card-text>
      </v-card>
        <v-snackbar v-model="snackbar" :color="snackbarColor" title>
          {{ snackbarText }}

          <template v-slot:action="{ attrs }">
            <v-btn color="dark" text v-bind="attrs" @click="snackbar = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import StateDropdown from "@/components/common/inputs/StateDropdown.vue";
import InternationalPhoneNumberInput from '@/components/common/inputs/InternationalPhoneNumberInput.vue';

import { UserInfo } from "@/types/User";
import affiliateService from "@/services/portal/affiliateService";

export default Vue.extend({
  name: "AffiliateInfo",
  components: {
    StateDropdown,
    InternationalPhoneNumberInput
  },
  data: function () {
    return {
      affiliateInfo: {} as any,
      originalAffiliateInfo: null as any,
      editMode: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "green",
    };
  },
  computed: {
      userInfo(): UserInfo {
          return this.$store.state.userInfo;
      },

      canBeRenewed(): boolean {
          if (!this.affiliateInfo.canRenew) return false;
          return true;
      },
  },
  methods: {
    toggleEditMode() {
      this.$data.originalAffiliateInfo = JSON.parse(JSON.stringify(this.$data.affiliateInfo));
      this.editMode = !this.editMode;
    },
    abortEdit() {
      this.$data.affiliateInfo = JSON.parse(JSON.stringify(this.$data.originalAffiliateInfo));
      this.$data.originalAffiliateInfo = null;
      this.editMode = false;
    },
    saveEdits() {
      affiliateService
        .editAffiliateById(
          this.affiliateId,
          this.userInfo.personId,
          this.affiliateInfo.contact.email,
          this.affiliateInfo.contact.phoneNumber,
          this.affiliateInfo.contact.address,
          this.affiliateInfo.contact.city,
          this.affiliateInfo.contact.state,
          this.affiliateInfo.contact.zipCode,
          this.affiliateInfo.clubSite
        )
        .then((res: any) => {
          if (res.success) {
            this.snackbar = true;
            this.snackbarColor = "green";
            this.snackbarText = "Successfully updated affiliate info";
          } else {
            this.snackbar = true;
            this.snackbarColor = "red";
            this.snackbarText = "Error updating affiliate info"
        }})
        .catch((e: any) => {
            this.snackbar = true;
            this.snackbarColor = "red";
            const descriptiveMessage = (e?.response ?? e)?.data?.error ?? null;
          this.snackbarText = descriptiveMessage
                              ? descriptiveMessage
                              : e?.message
                              ? e.message
                              : "Error updating affiliate info";
        });

      this.toggleEditMode();
    },
 
  },
  created: function () {
    if (this.affiliateId && this.userInfo) {
      affiliateService
        .getAffiliateById(this.affiliateId, this.userInfo.personId)
        .then((res) => {
          this.$data.affiliateInfo = res;
        })
        .catch(this.$handleError);
    }
  },
  watch: {
    affiliateId: function (newVal: String) {
      affiliateService
        .getAffiliateById(this.affiliateId, this.userInfo.personId)
        .then((res) => {
          this.$data.affiliateInfo = res;
        })
        .catch(this.$handleError);
    },
    userInfo: function (newVal: object) {
      affiliateService
        .getAffiliateById(this.affiliateId, this.userInfo.personId)
        .then((res) => {
          this.$data.affiliateInfo = res;
        })
        .catch(this.$handleError);
    },
  },
  props: {
    affiliateId: { type: String },
  },
});
</script>
